import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MeetingTemplateEditor, type MeetingTemplateDraft } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import {
  createMeetingTemplate,
  deleteMeetingTemplate,
  fetchMeetingTemplates,
  saveMeetingTemplate,
} from '@wundo/client/memberMeetingTemplates.ts';
import { MeetingTemplateBlocks } from '@/components/settings/MeetingTemplateBlocks.tsx';
import { MeetingTemplateEditScreen } from '@/components/settings/MeetingTemplateEditScreen.tsx';
import { useMeetingTemplateAudiences } from '@/components/settings/useMeetingTemplateAudiences.ts';
import { useMeetingTemplateReorder } from '@/components/settings/useMeetingTemplateReorder.ts';
import { meetingTemplateLabels } from '@/lib/meetingTemplateLabels.ts';
import type { MeetingTemplateKind } from '@wundo/shared/meetingTemplate.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings/templates')({
  component: TemplateSettingsPage,
});

/* Which of the two screens is open. A new template starts as its block's kind. */
type Screen =
  | { kind: 'list' }
  | { kind: 'edit'; templateId: string }
  | { kind: 'create'; templateKind: MeetingTemplateKind };

/**
 * The gesprekstypes screen: a block per kind of meeting, the organisation's
 * types first, then your own. What a row offers comes from the server with
 * every row, never from a role read here: a member reads the organisation's,
 * an admin keeps both.
 */
export function TemplateSettingsPage() {
  const queryClient = useQueryClient();
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const {
    data: templates,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetingTemplates', user?.id, 'manage'],
    queryFn: () => fetchMeetingTemplates(user!.id, 'manage'),
    enabled: Boolean(user?.id),
  });

  const keepsOrganisation = (templates ?? []).some(
    (template) => template.scope === 'organisation' && template.editable,
  );
  const { cohorts, tracks } = useMeetingTemplateAudiences(
    user?.organisation_id,
    keepsOrganisation,
  );

  /*
   * Every write goes back to the list and re-reads it from the server: a
   * template's own row is what the agenda and the picker read next, so nothing
   * here is worth guessing at locally.
   */
  const write = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setScreen({ kind: 'list' });
      await queryClient.invalidateQueries({ queryKey: ['meetingTemplates'] });
      await queryClient.invalidateQueries({ queryKey: ['meetingTemplate'] });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
    },
  });

  const reorder = useMeetingTemplateReorder(user?.id);

  if (isPending || !user) return null;
  if (isError) {
    return (
      <p className='text-base text-ink-muted'>{m.settings_templates_load_failed()}</p>
    );
  }

  const saveFailed = write.isError ? m.settings_template_save_failed() : undefined;
  const backToList = () => {
    setScreen({ kind: 'list' });
    write.reset();
  };

  const save = ({ trackId, cohortId, kind, scope, ...draft }: MeetingTemplateDraft) => {
    const audience = { track_id: trackId, cohort_id: cohortId };
    if (screen.kind === 'create') {
      const templateKind = kind ?? screen.templateKind;
      write.mutate(() =>
        createMeetingTemplate(user.id, {
          ...draft,
          /* Only a keeper of the organisation's types is asked; anyone else writes their own. */
          scope: scope ?? 'personal',
          kind: templateKind,
          ...audience,
        }),
      );
      return;
    }
    if (screen.kind === 'edit') {
      const templateId = screen.templateId;
      write.mutate(() =>
        saveMeetingTemplate(user.id, templateId, { ...draft, ...audience }),
      );
    }
  };

  /* A failure shows as the editor's error; the dialog just closes. */
  const remove = async (templateId: string) => {
    await write
      .mutateAsync(() => deleteMeetingTemplate(user.id, templateId))
      .catch(() => undefined);
  };

  if (screen.kind === 'create') {
    return (
      <MeetingTemplateEditor
        template={null}
        /* Asked once, here: a type for two kinds of meeting is two types. */
        kind={screen.templateKind}
        kindEditable
        scope={keepsOrganisation ? 'organisation' : undefined}
        tracks={tracks}
        cohorts={cohorts}
        saving={write.isPending}
        error={saveFailed}
        onSave={save}
        onCancel={backToList}
        labels={meetingTemplateLabels()}
      />
    );
  }

  if (screen.kind === 'edit') {
    return (
      <MeetingTemplateEditScreen
        userId={user.id}
        templateId={screen.templateId}
        tracks={tracks}
        cohorts={cohorts}
        saving={write.isPending}
        error={saveFailed}
        onSave={save}
        onDelete={remove}
        onCancel={backToList}
      />
    );
  }

  return (
    <MeetingTemplateBlocks
      templates={templates}
      keepsOrganisation={keepsOrganisation}
      moveFailed={reorder.isError}
      onReorder={(change) => reorder.mutate(change)}
      onOpen={(templateId) => setScreen({ kind: 'edit', templateId })}
      onCreate={(templateKind) => setScreen({ kind: 'create', templateKind })}
    />
  );
}
